import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Alert,
  Box,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { hasPermission, PERMISSIONS } from '../protectedRoute';

// Horarios de la tienda (se muestran en la app; editables aquí)
const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const defaultStoreHours = DAY_NAMES.map((name, dow) => ({
  dow,
  name,
  closed: dow === 0,
  open: '9:00 AM',
  close: '6:00 PM',
}));

export default function ConfigurationPage() {
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Horarios de la tienda
  const [storeHours, setStoreHours] = useState(defaultStoreHours);
  const [storeHoursNote, setStoreHoursNote] = useState('');

  const updateDay = (dow, patch) => {
    setStoreHours((prev) => prev.map((d) => (d.dow === dow ? { ...d, ...patch } : d)));
  };

  // Fechas especiales cerrado (feriados, cierres puntuales)
  const [specialDates, setSpecialDates] = useState([]);
  const [newSpecialDate, setNewSpecialDate] = useState('');
  const [newSpecialEnd, setNewSpecialEnd] = useState('');
  const [newSpecialLabel, setNewSpecialLabel] = useState('');

  const addSpecialDate = () => {
    if (!newSpecialDate) return;
    if (newSpecialEnd && newSpecialEnd < newSpecialDate) return;
    setSpecialDates((prev) => [
      ...prev,
      { date: newSpecialDate, endDate: newSpecialEnd || null, label: newSpecialLabel.trim() },
    ].sort((a, b) => a.date.localeCompare(b.date)));
    setNewSpecialDate('');
    setNewSpecialEnd('');
    setNewSpecialLabel('');
  };

  const removeSpecialDate = (idx) => {
    setSpecialDates((prev) => prev.filter((_, i) => i !== idx));
  };

  // Certificados: datos del club que salen en los certificados de cursos
  const canEditCerts = hasPermission(PERMISSIONS.EDIT_CONFIG);
  const [certAuthNumber, setCertAuthNumber] = useState('');
  const [certNameLocation, setCertNameLocation] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const db = getFirestore();
        const bookingSnap = await getDoc(doc(db, 'settings', 'booking'));
        if (bookingSnap.exists()) {
          const data = bookingSnap.data();
          if (data.maxAdvanceDays) setMaxAdvanceDays(data.maxAdvanceDays);
        }
        const certsSnap = await getDoc(doc(db, 'settings', 'certificates'));
        if (certsSnap.exists()) {
          const c = certsSnap.data();
          setCertAuthNumber(c.clubAuthorizationNumber || '');
          setCertNameLocation(c.clubNameLocation || '');
        }
        const hoursSnap = await getDoc(doc(db, 'settings', 'storeHours'));
        if (hoursSnap.exists()) {
          const h = hoursSnap.data();
          if (Array.isArray(h.days) && h.days.length === 7) setStoreHours(h.days);
          setStoreHoursNote(h.note || '');
          if (Array.isArray(h.specialDates)) setSpecialDates(h.specialDates);
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const db = getFirestore();
      await setDoc(
        doc(db, 'settings', 'booking'),
        {
          maxAdvanceDays: Number(maxAdvanceDays),
          updatedAt: new Date(),
        },
        { merge: true }
      );
      if (canEditCerts) {
        await setDoc(
          doc(db, 'settings', 'certificates'),
          {
            clubAuthorizationNumber: certAuthNumber.trim(),
            clubNameLocation: certNameLocation.trim(),
            updatedAt: new Date(),
          },
          { merge: true }
        );
      }
      await setDoc(
        doc(db, 'settings', 'storeHours'),
        {
          days: storeHours,
          note: storeHoursNote.trim(),
          specialDates,
          updatedAt: new Date(),
        },
        { merge: true }
      );
      setMessage({ type: 'success', text: 'Configuración guardada correctamente.' });
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Error guardando la configuración.' });
    }
    setSaving(false);
  };

  if (loading) return <Container><Typography>Cargando...</Typography></Container>;

  return (
    <>
      <Helmet>
        <title>Configuration | BBTC</title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Configuración
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 3 }}>{message.text}</Alert>
        )}

        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Reservaciones</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Define el límite máximo de días de anticipación con el que un cliente puede reservar.
              </Typography>
              <TextField
                label="Días máximos de anticipación"
                type="number"
                value={maxAdvanceDays}
                onChange={(e) => setMaxAdvanceDays(e.target.value)}
                inputProps={{ min: 1, max: 365 }}
                helperText="Ejemplo: 7 = el cliente solo puede reservar hasta 7 días en el futuro."
                fullWidth
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Horarios de la Tienda</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Estos horarios se muestran a los clientes en la app. Edítalos aquí si cambian.
              </Typography>
              <Stack spacing={1.5}>
                {storeHours.map((d) => (
                  <Stack key={d.dow} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <Typography sx={{ width: 110, fontWeight: 600 }}>{d.name}</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!d.closed}
                          onChange={(e) => updateDay(d.dow, { closed: !e.target.checked })}
                        />
                      }
                      label={d.closed ? 'Cerrado' : 'Abierto'}
                      sx={{ width: 120 }}
                    />
                    {!d.closed && (
                      <>
                        <TextField
                          size="small"
                          label="Abre"
                          value={d.open}
                          onChange={(e) => updateDay(d.dow, { open: e.target.value })}
                          placeholder="9:00 AM"
                          sx={{ width: 140 }}
                        />
                        <TextField
                          size="small"
                          label="Cierra"
                          value={d.close}
                          onChange={(e) => updateDay(d.dow, { close: e.target.value })}
                          placeholder="6:00 PM"
                          sx={{ width: 140 }}
                        />
                      </>
                    )}
                  </Stack>
                ))}
                <TextField
                  label="Nota (opcional)"
                  value={storeHoursNote}
                  onChange={(e) => setStoreHoursNote(e.target.value)}
                  placeholder="Ej: Días feriados cerramos temprano"
                  fullWidth
                  size="small"
                />

                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Fechas especiales cerrado (feriados)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: '0 !important' }}>
                  Estas fechas salen en la app como cerrado, con su motivo. Ej: 25 de diciembre — Navidad.
                </Typography>
                {specialDates.map((sd, idx) => (
                  <Stack key={`${sd.date}-${idx}`} direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ fontWeight: 600, minWidth: 200 }}>
                      {sd.date}{sd.endDate ? ` → ${sd.endDate}` : ''}
                    </Typography>
                    <Typography color="text.secondary" sx={{ flex: 1 }}>{sd.label || 'Cerrado'}</Typography>
                    <Button size="small" color="error" onClick={() => removeSpecialDate(idx)}>
                      Quitar
                    </Button>
                  </Stack>
                ))}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <TextField
                    size="small"
                    type="date"
                    label="Fecha"
                    value={newSpecialDate}
                    onChange={(e) => setNewSpecialDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 170 }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Hasta (opcional)"
                    value={newSpecialEnd}
                    onChange={(e) => setNewSpecialEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 170 }}
                  />
                  <TextField
                    size="small"
                    label="Motivo"
                    value={newSpecialLabel}
                    onChange={(e) => setNewSpecialLabel(e.target.value)}
                    placeholder="Ej: Navidad"
                    sx={{ flex: 1 }}
                  />
                  <Button variant="outlined" onClick={addSpecialDate} disabled={!newSpecialDate}>
                    Añadir
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {canEditCerts && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>Certificados</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Datos del club u organización que salen en los certificados de los cursos.
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Número de autorización del club u organización"
                    value={certAuthNumber}
                    onChange={(e) => setCertAuthNumber(e.target.value)}
                    placeholder="Ej: 222118"
                    fullWidth
                  />
                  <TextField
                    label="Nombre y localización del club, organización o institución"
                    value={certNameLocation}
                    onChange={(e) => setCertNameLocation(e.target.value)}
                    multiline
                    minRows={3}
                    placeholder={'B&B TARGET CENTER INC.\n369 SAN CLAUDIO AVE.\nSAN JUAN, PR. 00926'}
                    helperText="Se imprime tal cual, línea por línea."
                    fullWidth
                  />
                </Stack>
              </CardContent>
            </Card>
          )}

          <Box>
            <Button variant="contained" size="large" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </Box>

          <Alert severity="info">
            Los precios y depósitos ahora se configuran <b>por categoría</b>, no globalmente.
            Ve a <b>Reservations → Categories</b> para configurar precios y depósitos.
          </Alert>
        </Stack>
      </Container>
    </>
  );
}
